import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function InquiryPage() {
  const { cart, updateItem, removeItem, clearCart, itemCount } = useCart();
  const { user } = useAuth(); // optional — used only to prefill fields if someone happens to be logged in

  const [step, setStep] = useState('review'); // 'review' | 'contact'

  const [name,    setName]    = useState(user?.name || '');
  const [email,   setEmail]   = useState(user?.email || '');
  const [mobile,  setMobile]  = useState(user?.mobile || '');
  const [address, setAddress] = useState(user?.address || '');
  const [notes,      setNotes]      = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(null);

  const items = cart?.items || [];

  const handleContinue = () => {
    if (!items.length) { toast.error('Your inquiry list is empty.'); return; }
    setStep('contact');
  };

  const handleSubmit = async () => {
    if (!items.length) { toast.error('Your inquiry list is empty.'); return; }
    if (!name.trim() || !email.trim() || !mobile.trim()) {
      toast.error('Name, email, and mobile number are required.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim(),
        mobile: mobile.trim(),
        address: address.trim(),
        notes,
        items: items.map((i) => ({
          product:     i.productId || undefined,
          productName: i.productName,
          sizeLabel:   i.sizeLabel || '',
          quantity:    i.quantity,
        })),
      };

      const r = await api.post('/inquiries', payload);
      setSubmitted(r.data.data);
      clearCart();

      if (r.data.pdfBase64) {
        const bytes = atob(r.data.pdfBase64);
        const arr   = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        const blob = new Blob([arr], { type: 'application/pdf' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `${r.data.data.inquiryNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
      toast.success('Inquiry submitted! Check your email.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-20 flex items-center justify-center px-6">
          <div className="max-w-lg w-full text-center py-20 animate-fade-in">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="material-symbols-outlined text-[40px] text-green-600">check_circle</span>
            </div>
            <h1 className="font-headline font-black text-4xl uppercase tracking-tight text-on-surface mb-2">
              Inquiry Sent!
            </h1>
            <p className="font-body text-secondary mb-6 leading-relaxed">
              Your inquiry <span className="font-bold text-primary-container">{submitted.inquiryNumber}</span> has been submitted.
              A confirmation PDF has been emailed to <span className="font-semibold">{submitted.userEmail}</span> and downloaded to your device.
            </p>

            <div className="bg-surface-container-low border border-outline-variant/30 text-left mb-8">
              <div className="px-5 py-3 bg-on-surface">
                <span className="font-label text-xs uppercase tracking-widest text-gray-400">Inquiry Summary</span>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-outline-variant/20">
                    <th className="px-5 py-3 font-label text-[10px] uppercase tracking-widest text-secondary text-left">Sr.</th>
                    <th className="px-5 py-3 font-label text-[10px] uppercase tracking-widest text-secondary text-left">Product</th>
                    <th className="px-5 py-3 font-label text-[10px] uppercase tracking-widest text-secondary text-right">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {submitted.items.map((item, i) => (
                    <tr key={i} className="border-b border-surface-container last:border-0">
                      <td className="px-5 py-3 font-body text-sm text-secondary">{i + 1}</td>
                      <td className="px-5 py-3">
                        <p className="font-label text-sm font-bold text-on-surface">{item.productName}</p>
                        {item.sizeLabel && <p className="font-label text-xs text-secondary">Size: {item.sizeLabel}</p>}
                      </td>
                      <td className="px-5 py-3 font-label text-sm font-bold text-on-surface text-right">{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products" className="btn-secondary justify-center">
                <span className="material-symbols-outlined">storefront</span>
                Continue Browsing
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1 pt-20">
        <div className="bg-on-surface text-white px-6 md:px-10 py-10">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-2 font-label text-xs text-gray-500 uppercase tracking-widest mb-3">
              <Link to="/" className="hover:text-gray-300">Home</Link>
              <span>/</span>
              <span className="text-gray-400">Submit Inquiry</span>
            </div>
            <h1 className="font-headline font-black text-4xl md:text-5xl uppercase tracking-tight">Submit Inquiry</h1>
            <p className="font-label text-sm text-gray-400 mt-2">{itemCount} item{itemCount !== 1 ? 's' : ''} in your list</p>

            {/* Step indicator */}
            <div className="flex items-center gap-3 mt-5">
              <div className={`flex items-center gap-2 font-label text-xs uppercase tracking-widest ${step === 'review' ? 'text-white' : 'text-gray-500'}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${step === 'review' ? 'bg-primary-container text-white' : 'bg-white/10'}`}>1</span>
                Review Products
              </div>
              <span className="text-gray-600">—</span>
              <div className={`flex items-center gap-2 font-label text-xs uppercase tracking-widest ${step === 'contact' ? 'text-white' : 'text-gray-500'}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold ${step === 'contact' ? 'bg-primary-container text-white' : 'bg-white/10'}`}>2</span>
                Your Details
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 py-12">
          {items.length === 0 ? (
            <div className="text-center py-24">
              <span className="material-symbols-outlined text-[80px] text-outline-variant">list_alt</span>
              <h2 className="font-headline font-black text-3xl uppercase mt-4 text-secondary">Your list is empty</h2>
              <p className="font-body text-sm text-secondary mt-2 mb-8">Browse products and add items to get started.</p>
              <Link to="/products" className="btn-primary">Browse Products</Link>
            </div>
          ) : step === 'review' ? (
            /* ── STEP 1: Review products only ───────────────────────────── */
            <div className="max-w-screen-lg mx-auto">
              <h2 className="font-headline font-black text-2xl uppercase tracking-tight mb-6">Review Items</h2>

              <div className="hidden md:grid grid-cols-12 px-4 py-2 bg-surface-container font-label text-[10px] uppercase tracking-widest text-secondary gap-4 mb-2">
                <span className="col-span-1">Sr.</span>
                <span className="col-span-6">Product</span>
                <span className="col-span-3 text-center">Qty</span>
                <span className="col-span-2" />
              </div>

              <div className="space-y-3 mb-8">
                {items.map((item, i) => (
                  <div key={item.id} className="bg-white border border-outline-variant/20 p-4 flex items-center gap-4">
                    <span className="font-headline font-black text-2xl text-outline-variant w-6 text-center flex-shrink-0">{i + 1}</span>

                    <div className="flex-1 min-w-0">
                      <p className="font-headline font-bold text-base uppercase tracking-tight text-on-surface">{item.productName}</p>
                      {item.sizeLabel && <p className="font-label text-xs text-secondary mt-0.5">Size: {item.sizeLabel}</p>}
                    </div>

                    <div className="flex items-center border border-outline-variant flex-shrink-0">
                      <button onClick={() => updateItem(item.id, Math.max(1, item.quantity - 1))}
                        className="w-9 h-9 flex items-center justify-center text-secondary hover:bg-surface-container transition-colors">
                        <span className="material-symbols-outlined text-[16px]">remove</span>
                      </button>
                      <span className="w-10 text-center font-label font-bold text-sm">{item.quantity}</span>
                      <button onClick={() => updateItem(item.id, item.quantity + 1)}
                        className="w-9 h-9 flex items-center justify-center text-secondary hover:bg-surface-container transition-colors">
                        <span className="material-symbols-outlined text-[16px]">add</span>
                      </button>
                    </div>

                    <button onClick={() => removeItem(item.id)}
                      className="p-2 text-outline-variant hover:text-error transition-colors flex-shrink-0">
                      <span className="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  </div>
                ))}
              </div>

              <div className="bg-on-surface text-white p-6 flex items-center justify-between flex-wrap gap-4">
                <div className="flex gap-8">
                  <div>
                    <p className="font-label text-[10px] uppercase tracking-widest text-gray-400">Total Items</p>
                    <p className="font-headline font-black text-2xl">{itemCount}</p>
                  </div>
                  <div>
                    <p className="font-label text-[10px] uppercase tracking-widest text-gray-400">Product Lines</p>
                    <p className="font-headline font-black text-2xl">{items.length}</p>
                  </div>
                </div>
                <button onClick={handleContinue}
                  className="bg-primary-container hover:bg-primary text-white font-headline font-bold uppercase tracking-wider px-8 py-4 flex items-center gap-2 transition-colors">
                  Continue <span className="material-symbols-outlined">arrow_forward</span>
                </button>
              </div>
            </div>
          ) : (
            /* ── STEP 2: Contact details + real submit ─────────────────── */
            <div className="max-w-screen-md mx-auto">
              <button onClick={() => setStep('review')}
                className="flex items-center gap-2 font-label text-xs uppercase tracking-widest text-secondary hover:text-primary-container mb-6 transition-colors">
                <span className="material-symbols-outlined text-[16px]">arrow_back</span> Back to Products
              </button>

              <div className="bg-white border border-outline-variant/20 p-6 mb-6">
                <h3 className="font-headline font-black text-lg uppercase tracking-tight mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary-container text-[22px]">person</span>
                  Your Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="input-label">Name *</label>
                    <input value={name} onChange={e => setName(e.target.value)} className="input-field" placeholder="Your full name" />
                  </div>
                  <div>
                    <label className="input-label">Email *</label>
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="input-field" placeholder="you@example.com" />
                  </div>
                  <div>
                    <label className="input-label">Mobile *</label>
                    <input value={mobile} onChange={e => setMobile(e.target.value)} className="input-field" placeholder="10-digit mobile number" />
                  </div>
                  <div>
                    <label className="input-label">Address (optional)</label>
                    <input value={address} onChange={e => setAddress(e.target.value)} className="input-field" placeholder="Delivery address" />
                  </div>
                </div>
              </div>

              <div className="bg-white border border-outline-variant/20 p-6 mb-6">
                <label className="input-label">Additional Notes</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)}
                  rows={4}
                  className="input-field resize-none mt-1"
                  placeholder="Special requirements, delivery notes, grade preferences..." />
              </div>

              <div className="bg-on-surface text-white p-6">
                <h3 className="font-headline font-black text-lg uppercase tracking-tight mb-4">Inquiry Summary</h3>
                <div className="space-y-2 mb-6">
                  <div className="flex justify-between font-label text-sm">
                    <span className="text-gray-400">Total Items</span>
                    <span className="font-bold">{itemCount}</span>
                  </div>
                  <div className="flex justify-between font-label text-sm">
                    <span className="text-gray-400">Product Lines</span>
                    <span className="font-bold">{items.length}</span>
                  </div>
                </div>

                <button onClick={handleSubmit} disabled={submitting}
                  className="w-full bg-primary-container hover:bg-primary text-white font-headline font-bold uppercase tracking-wider py-4 text-base flex items-center justify-center gap-2 transition-colors disabled:opacity-70">
                  {submitting
                    ? <><span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Submitting...</>
                    : <><span className="material-symbols-outlined">send</span> Submit Inquiry</>
                  }
                </button>
                <p className="font-label text-[10px] text-gray-500 mt-3 text-center uppercase tracking-widest">
                  PDF will be emailed & auto-downloaded
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}